import { useEffect, useMemo, useState } from "react";

import { SubscriptionTier } from "@repo/ui/pricing-hooks";
import {
  Button,
  CancellationSurveySubmitted,
  Checkbox,
  Modal,
  SelectButton,
} from "@shapr3d/frontend-lib";
import { shuffle } from "es-toolkit";

import { useTracker } from "../../../hooks/useTracker";
import useTranslation from "../../../hooks/useTranslation";
import {
  ElementClickAction,
  ElementClickLocation,
  ModalClosedAction,
  ModalShownAction,
} from "../../../lib/TrackingAdapter";
import { getTierDisplayName } from "../subscriptionUtils";

import style from "./CancellationSurveyModal.module.css";

type SurveyProps = ConstructorParameters<typeof CancellationSurveySubmitted>[0];
type ReasonValue = Exclude<keyof SurveyProps, "alternative" | "missingFeature">;
type Alternative = NonNullable<SurveyProps["alternative"]>;
type MissingFeature = NonNullable<SurveyProps["missingFeature"]>;

interface CancellationSurveyModalProps {
  open: boolean;
  onClose: () => void;
  onCancelSubscription: () => void;
  isLoading: boolean;
  subscriptionTier: SubscriptionTier;
  subscriptionExpirationDate: string;
}

export const CancellationSurveyModal: React.FC<CancellationSurveyModalProps> = ({
  open,
  onClose,
  onCancelSubscription,
  isLoading,
  subscriptionTier,
}) => {
  const { t, m } = useTranslation();
  const tracker = useTracker();

  const CANCELLATION_REASONS: Array<{ value: ReasonValue; title: string }> = useMemo(
    () =>
      [
        {
          value: "changeSubscription",
          title: t(m.CancellationSurveyModal.ChangeSubscription),
        },
        { value: "tooExpensive", title: t(m.CancellationSurveyModal.TooExpensive) },
        {
          value: "foundAlternative",
          title: t(m.CancellationSurveyModal.FoundAlternative),
        },
        { value: "notUsingEnough", title: t(m.CancellationSurveyModal.NotUsingEnough) },
        { value: "missingFeatures", title: t(m.CancellationSurveyModal.MissingFeatures) },
        { value: "technicalIssues", title: t(m.CancellationSurveyModal.TechnicalIssues) },
      ] satisfies Array<{ value: ReasonValue; title: string }>,
    [t, m]
  );

  const ALTERNATIVES: { value: Alternative; title: string }[] = useMemo(
    () => [
      { value: "fusion", title: t(m.CancellationSurveyModal.Fusion) },
      { value: "sketchup", title: t(m.CancellationSurveyModal.SketchUp) },
      { value: "solidworks", title: t(m.CancellationSurveyModal.SolidWorks) },
    ],
    [t, m]
  );

  const MISSING_FEATURES: {
    value: MissingFeature;
    title: string;
  }[] = useMemo(
    () => [
      { value: "assemblies", title: t(m.CancellationSurveyModal.Assemblies) },
      { value: "bill_of_materials", title: t(m.CancellationSurveyModal.BillOfMaterials) },
      { value: "threads", title: t(m.CancellationSurveyModal.Threads) },
    ],
    [t, m]
  );

  const [selectedReasons, setSelectedReasons] = useState<ReasonValue[]>([]);
  const [selectedAlternative, setSelectedAlternative] = useState<Alternative | "">("");
  const [selectedMissingFeature, setSelectedMissingFeature] = useState<MissingFeature | "">("");
  const [shuffledReasons, setShuffledReasons] = useState(() => shuffle(CANCELLATION_REASONS));
  const [shuffledAlternatives, setShuffledAlternatives] = useState(() => shuffle(ALTERNATIVES));
  const [shuffledMissingFeatures, setShuffledMissingFeatures] = useState(() =>
    shuffle(MISSING_FEATURES)
  );

  useEffect(() => {
    if (open) {
      setSelectedReasons([]);
      setSelectedAlternative("");
      setSelectedMissingFeature("");
      setShuffledReasons(shuffle(CANCELLATION_REASONS));
      setShuffledAlternatives(shuffle(ALTERNATIVES));
      setShuffledMissingFeatures(shuffle(MISSING_FEATURES));
    }
  }, [open, tracker, CANCELLATION_REASONS, MISSING_FEATURES, ALTERNATIVES]);

  const showAlternatives = selectedReasons.includes("foundAlternative");
  const showMissingFeatures = selectedReasons.includes("missingFeatures");
  const isSubmitEnabled =
    selectedReasons.length > 0 &&
    (!showAlternatives || selectedAlternative !== "") &&
    (!showMissingFeatures || selectedMissingFeature !== "");

  const toggleReason = (value: ReasonValue) => {
    const isDeselecting = selectedReasons.includes(value);
    setSelectedReasons((prev) =>
      isDeselecting ? prev.filter((v) => v !== value) : [...prev, value]
    );
    if (isDeselecting) {
      if (value === "foundAlternative") setSelectedAlternative("");
      if (value === "missingFeatures") setSelectedMissingFeature("");
    }
  };

  const handleSubmit = () => {
    tracker.trackCancellationSurveySubmitted({
      changeSubscription: selectedReasons.includes("changeSubscription"),
      tooExpensive: selectedReasons.includes("tooExpensive"),
      foundAlternative: selectedReasons.includes("foundAlternative"),
      notUsingEnough: selectedReasons.includes("notUsingEnough"),
      missingFeatures: selectedReasons.includes("missingFeatures"),
      technicalIssues: selectedReasons.includes("technicalIssues"),
      other: selectedReasons.includes("other"),
      ...(showAlternatives && selectedAlternative && { alternative: selectedAlternative }),
      ...(showMissingFeatures &&
        selectedMissingFeature && { missingFeature: selectedMissingFeature }),
    });

    tracker.trackButtonClick({
      action: ElementClickAction.CONFIRM,
      location: ElementClickLocation.CANCELLATION_SURVEY_MODAL,
    });

    onCancelSubscription();
  };

  return (
    <Modal
      open={open}
      size="medium"
      onOpen={() => tracker.trackModalShown(ModalShownAction.CANCELLATION_SURVEY)}
      onClose={() => {
        tracker.trackModalClosed(ModalClosedAction.CANCELLATION_SURVEY);
        onClose();
      }}
      title={t(m.CancellationSurveyModal.WhyCancelling)}
      footer={
        <div className={style.footer}>
          <Button
            onClick={() => {
              tracker.trackButtonClick({
                action: ElementClickAction.CANCEL,
                location: ElementClickLocation.CANCELLATION_SURVEY_MODAL,
              });
              onClose();
            }}
            disabled={isLoading}
            variant="grayscale"
          >
            {t(m.CancellationSurveyModal.KeepTier, {
              tierName: getTierDisplayName(subscriptionTier),
            })}
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading || !isSubmitEnabled} variant="red">
            {t(m.CancellationSurveyModal.CancelSubscription)}
          </Button>
        </div>
      }
    >
      <div className={style.container}>
        <div className={style.content}>
          <p className={style.context}>{t(m.CancellationSurveyModal.Context)}</p>

          <ul className={style.reasonList}>
            {[...shuffledReasons].map((reason) => (
              <li key={reason.value} className={style.reasonItem}>
                <Checkbox
                  id={`reason-${reason.value}`}
                  checked={selectedReasons.includes(reason.value)}
                  onChange={() => toggleReason(reason.value)}
                />
                <label htmlFor={`reason-${reason.value}`} className={style.reasonLabel}>
                  {reason.title}
                </label>
              </li>
            ))}
            <li className={style.reasonItem}>
              <Checkbox
                id={`reason-other`}
                checked={selectedReasons.includes("other")}
                onChange={() => toggleReason("other")}
              />
              <label htmlFor={`reason-other`} className={style.reasonLabel}>
                {t(m.CancellationSurveyModal.OtherReason)}
              </label>
            </li>
          </ul>

          {showAlternatives && (
            <div className={style.followUpSection}>
              <p className={style.followUpLabel}>{t(m.CancellationSurveyModal.WhichAlternative)}</p>
              <SelectButton
                type="radio"
                value={selectedAlternative}
                onChange={(value: string) => setSelectedAlternative(value as Alternative)}
                size="small"
              >
                {shuffledAlternatives.map((alt) => (
                  <SelectButton.Item key={alt.value} title={alt.title} value={alt.value} />
                ))}
                <SelectButton.Item title={t(m.CancellationSurveyModal.Other)} value="other" />
              </SelectButton>
            </div>
          )}

          {showMissingFeatures && (
            <div className={style.followUpSection}>
              <p className={style.followUpLabel}>{t(m.CancellationSurveyModal.WhichFeatures)}</p>
              <SelectButton
                type="radio"
                value={selectedMissingFeature}
                onChange={(value: string) => setSelectedMissingFeature(value as MissingFeature)}
                size="small"
              >
                {shuffledMissingFeatures.map((feature) => (
                  <SelectButton.Item
                    key={feature.value}
                    title={feature.title}
                    value={feature.value}
                  />
                ))}
                <SelectButton.Item title={t(m.CancellationSurveyModal.Other)} value="other" />
              </SelectButton>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
